import { Search, SlidersHorizontal } from 'lucide-react';
import { useState } from 'react';
import { useQuery } from '@apollo/client';
import type { PackageSearchFilters } from '../types/marketplaceSearch';
import { EMPTY_PACKAGE_SEARCH_FILTERS } from '../types/marketplaceSearch';
import { CATEGORY_LABELS } from '../types/package';
import { GET_AIRLINES } from '../graphql/queries/airline';

const CATEGORY_OPTIONS = Object.entries(CATEGORY_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const MONTHS = [
  'Janvier',
  'Février',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juillet',
  'Août',
  'Septembre',
  'Octobre',
  'Novembre',
  'Décembre',
] as const;

const DURATION_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Toutes les durées' },
  { value: '1-3', label: '1 à 3 jours' },
  { value: '4-7', label: '4 à 7 jours' },
  { value: '8-14', label: '8 à 14 jours' },
  { value: '15+', label: '15 jours et +' },
];

const fieldClass =
  'w-full rounded border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600';

const labelClass = 'mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-800';

type FieldsProps = {
  value: PackageSearchFilters;
  patch: (partial: Partial<PackageSearchFilters>) => void;
  airlinesLoading: boolean;
  airlines: Array<{ id: number; name: string; code?: string | null }>;
  onChange: (next: PackageSearchFilters) => void;
  resetUrl: () => void;
};

function OfferFilterFields({
  value,
  patch,
  airlinesLoading,
  airlines,
  onChange,
  resetUrl,
}: FieldsProps) {
  return (
    <>
      <div className="border-b border-gray-200 pb-4">
        <label className={labelClass} htmlFor="sejours-cat">
          Thématique
        </label>
        <p className="mb-2 text-[11px] leading-snug text-gray-500">
          Culture, plage, nature, omra…
        </p>
        <select
          id="sejours-cat"
          value={value.category}
          onChange={(e) => patch({ category: e.target.value })}
          className={fieldClass}
          title="Choisissez le style de voyage recherché"
        >
          <option value="">Toutes les thématiques</option>
          {CATEGORY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className="border-b border-gray-200 py-4">
        <label className={labelClass} htmlFor="sejours-dest">
          Destination ou mot-clé
        </label>
        <input
          id="sejours-dest"
          type="text"
          placeholder="Ville, pays, titre…"
          value={value.destination}
          onChange={(e) => patch({ destination: e.target.value })}
          className={fieldClass}
        />
      </div>

      <div className="border-b border-gray-200 py-4">
        <label className={labelClass} htmlFor="sejours-month">
          Mois de départ
        </label>
        <select
          id="sejours-month"
          value={value.month}
          onChange={(e) => patch({ month: e.target.value })}
          className={fieldClass}
        >
          <option value="">Tous les mois</option>
          {MONTHS.map((m, index) => (
            <option key={m} value={String(index + 1)}>
              {m}
            </option>
          ))}
        </select>
      </div>

      <div className="border-b border-gray-200 py-4">
        <label className={labelClass} htmlFor="sejours-dur">
          Durée du séjour
        </label>
        <select
          id="sejours-dur"
          value={value.duration}
          onChange={(e) => patch({ duration: e.target.value })}
          className={fieldClass}
        >
          {DURATION_OPTIONS.map((opt) => (
            <option key={opt.label + opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className="py-4">
        <label className={labelClass} htmlFor="sejours-airline">
          Compagnie aérienne
        </label>
        <select
          id="sejours-airline"
          value={value.airlineId}
          onChange={(e) => patch({ airlineId: e.target.value })}
          className={fieldClass}
          disabled={airlinesLoading}
        >
          <option value="">Toutes</option>
          {airlines.map((a) => (
            <option key={a.id} value={String(a.id)}>
              {a.name}
              {a.code ? ` (${a.code})` : ''}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-2 border-t border-gray-200 pt-4">
        <button
          type="submit"
          className="flex w-full items-center justify-center gap-2 rounded bg-blue-600 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
        >
          <Search className="h-4 w-4 shrink-0" />
          Rechercher
        </button>
        <button
          type="button"
          onClick={() => {
            onChange(EMPTY_PACKAGE_SEARCH_FILTERS);
            resetUrl();
          }}
          className="w-full rounded border border-gray-300 bg-white py-2.5 text-sm font-medium text-blue-700 hover:bg-gray-50"
        >
          Effacer les filtres
        </button>
      </div>
    </>
  );
}

type Props = {
  value: PackageSearchFilters;
  onChange: (next: PackageSearchFilters) => void;
  onSubmit: () => void;
  onReset: () => void;
};

export default function OffersFilters({ value, onChange, onSubmit, onReset }: Props) {
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const { data: airlinesData, loading: airlinesLoading } = useQuery(GET_AIRLINES);
  const airlines = airlinesData?.airlines ?? [];

  const patch = (partial: Partial<PackageSearchFilters>) => {
    onChange({ ...value, ...partial });
  };

  const shared: FieldsProps = {
    value,
    patch,
    airlinesLoading,
    airlines,
    onChange,
    resetUrl: onReset,
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="w-full"
    >
      {/* Bandeau mobile (style Booking : ouvrir les filtres) */}
      <button
        type="button"
        onClick={() => setMobileFiltersOpen((o) => !o)}
        className="mb-3 flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-4 py-3 text-left text-sm font-semibold text-gray-900 shadow-sm lg:hidden"
      >
        <span className="flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4 text-blue-600" />
          Filtres
        </span>
        <span className="text-xs font-normal text-blue-600">
          {mobileFiltersOpen ? 'Masquer' : 'Afficher'}
        </span>
      </button>

      {/* Panneau unique : colonne gauche desktop, repliable sur mobile */}
      <div
        className={[
          'rounded-lg border border-gray-300 bg-white shadow-sm lg:sticky lg:top-24 lg:self-start',
          mobileFiltersOpen ? 'block' : 'hidden',
          'lg:block',
        ].join(' ')}
      >
        <div className="hidden border-b border-gray-200 px-4 py-3 lg:block">
          <h2 className="text-sm font-bold text-gray-900">Filtrer les résultats</h2>
          <p className="mt-0.5 text-xs text-gray-500">Affinez votre recherche</p>
        </div>
        <div className="max-h-[min(70vh,calc(100vh-7rem))] overflow-y-auto px-4 pb-4 pt-3 lg:max-h-[calc(100vh-8rem)] lg:pt-2">
          <OfferFilterFields {...shared} />
        </div>
      </div>
    </form>
  );
}
